import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = ['submitButton', 'intro', 'errorMessage'];

	/**
	 * Regular expressions used for form field validation
	 */
	validationRules = {
		email: new RegExp(/^[A-Za-z0-9_!#$%&'*+\/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/, 'i'),
		password: new RegExp(/^(.+)$/, 'i'),
		string: new RegExp(/^(.+)$/, 'i'),
		select: new RegExp(/^(.+)$/, 'i'),
		hidden: new RegExp(/^(.+)$/, 'i'),
	};

	svgValid =
		'<div class="valid hidden absolute text-emercegreen-100 w-3 h-2 right-4 bottom-6 lg:bottom-6"><svg fill="currentColor" version="1.1" viewBox="0 0 17 13" xmlns="http://www.w3.org/2000/svg"><g fill-rule="evenodd"><g transform="translate(-103 -1255)" fill-rule="nonzero" stroke-width=".5"><g transform="translate(103.38 1255.1)"><polygon points="0 6.4 1.6 4.8 5.6 8.8 14.4 0 16 1.6 5.6 12"/></g></g></g></svg></div>';
	svgNotValid =
		'<div class="notvalid hidden absolute text-emerceorange-100 w-2 h-2 right-4 bottom-6 lg:bottom-6 "><svg enable-background="new 0 0 212.982 212.982" fill="currentColor" viewBox="0 0 212.98 212.98" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path d="m131.8 106.49 75.936-75.936c6.99-6.99 6.99-18.323 0-25.312-6.99-6.99-18.322-6.99-25.312 0l-75.937 75.937-75.937-75.938c-6.99-6.99-18.322-6.99-25.312 0-6.989 6.99-6.989 18.323 0 25.312l75.937 75.936-75.937 75.937c-6.989 6.99-6.989 18.323 0 25.312 6.99 6.99 18.322 6.99 25.312 0l75.937-75.937 75.937 75.937c6.989 6.99 18.322 6.99 25.312 0s6.99-18.322 0-25.312l-75.936-75.936z" clip-rule="evenodd" fill="" fill-rule="evenodd"/></svg></div>';

	svgLoading =
		'<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" xml:space="preserve" viewBox="0 0 40 40" width="32" height="32"><path opacity=".2" fill="#fff" d="M20.2 5.2a15 15 0 1 0 0 29.9 15 15 0 0 0 0-30zm0 26.5a11.6 11.6 0 1 1 0-23.2 11.6 11.6 0 0 1 0 23.2z"/><path fill="#fff" d="m27.3 11 2-2.7c-2-1.5-4.4-2.6-7-3l-.5 3.3c2 .3 4 1.1 5.5 2.4z"><animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="0.5s" repeatCount="indefinite"/></path></svg>';

	iconValid;
	iconNotValid;

	submitButtonLabel;

	initialize() {
		super.initialize();

		this.element.controller = this;

		let template = document.createElement('template');

		template.innerHTML = this.svgValid;
		this.iconValid = template.content.firstChild;

		template.innerHTML = this.svgNotValid;
		this.iconNotValid = template.content.firstChild;

		this.initFields();
	}

	connect() {
		this.submitButtonLabel = this.submitButtonTarget.innerHTML;
	}

	initFields() {
		this.element.querySelectorAll('input, select').forEach((formElement) => {
			if (!formElement.dataset.skipValidation) {
				formElement.dataset.valid = false;
				formElement.after(this.iconValid.cloneNode(true));
				formElement.after(this.iconNotValid.cloneNode(true));
			}
		});
	}

	validate(e) {
		if (this.isValid(e.currentTarget.dataset.fieldType, e.currentTarget.value)) {
			this.markAsValid(e.currentTarget);
		} else {
			this.markAsNotValid(e.currentTarget);
		}
	}

	validateAll() {
		let allValid = true;

		this.element.querySelectorAll('input, select').forEach((formElement) => {
			if (!formElement.dataset.skipValidation) {
				if (this.isValid(formElement.dataset.fieldType, formElement.value)) {
					this.markAsValid(formElement);
				} else {
					allValid = false;
					this.markAsNotValid(formElement);
				}
			}
		}, this);

		return allValid;
	}

	resetFields() {
		this.element.querySelectorAll('input, select').forEach((formElement) => {
			if (!formElement.dataset.skipValidation) {
				formElement.parentNode.querySelector('.valid').classList.add('hidden');
				formElement.parentNode.querySelector('.notvalid').classList.add('hidden');
				formElement.classList.replace('border-emercegreen-100', 'border-neutral-300');
				formElement.classList.replace('border-emerceorange-100', 'border-neutral-300');
			}
		});
	}

	setErrorState(message) {
		this.introTarget.classList.add('hidden');
		this.errorMessageTarget.innerText = message;
		this.errorMessageTarget.classList.remove('hidden');
		this.resetFields();
	}

	removeErrorState() {
		// this.introTarget.classList.remove('hidden');
		this.errorMessageTarget.classList.add('hidden');
	}

	isValid(type, value) {
		return this.validationRules[type].test(value);
	}

	markAsValid(element) {
		element.classList.replace('border-neutral-300', 'border-emercegreen-100');
		element.classList.replace('border-emerceorange-100', 'border-emercegreen-100');
		element.parentNode.querySelector('.valid').classList.remove('hidden');
		element.parentNode.querySelector('.notvalid').classList.add('hidden');
	}

	markAsNotValid(element) {
		element.classList.replace('border-neutral-300', 'border-emerceorange-100');
		element.classList.replace('border-emercegreen-100', 'border-emerceorange-100');
		element.parentNode.querySelector('.valid').classList.add('hidden');
		element.parentNode.querySelector('.notvalid').classList.remove('hidden');
	}

	setLoadingState() {
		this.submitButtonTarget.style.width = `${this.submitButtonTarget.offsetWidth}px`;
		this.submitButtonTarget.setAttribute('disabled', 'disabled');
		this.submitButtonTarget.classList.replace('btn-blue', 'btn-blue--loading');
		this.submitButtonTarget.innerHTML = this.svgLoading;
	}

	removeLoadingState() {
		this.submitButtonTarget.removeAttribute('disabled');
		this.submitButtonTarget.classList.replace('btn-blue--loading', 'btn-blue');
		this.submitButtonTarget.innerHTML = this.submitButtonLabel;
	}
}
